import { useState, useEffect, useCallback, memo } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface DebouncedSearchInputProps {
  /** Current search value from parent (used for controlled reset) */
  value: string;
  /** Callback fired after debounce delay with the search value */
  onValueChange: (value: string) => void;
  /** Debounce delay in milliseconds (default: 300ms) */
  delay?: number;
  /** Placeholder text */
  placeholder?: string;
  /** Additional class names for the container */
  className?: string;
  /** Additional class names for the input */
  inputClassName?: string;
  /**
   * The input's accessible name, when the placeholder is not enough (a placeholder is not a label). PT-7's
   * Items search and PT-5's Post-dialog line filter pass one; the older consumers pass nothing.
   */
  ariaLabel?: string;
}

/**
 * A search input that debounces value changes to prevent excessive parent re-renders.
 * The input maintains its own local state for responsive typing, and only updates
 * the parent after the debounce delay.
 */
export const DebouncedSearchInput = memo(function DebouncedSearchInput({
  value,
  onValueChange,
  delay = 300,
  placeholder = 'Search...',
  className,
  inputClassName,
  ariaLabel,
}: DebouncedSearchInputProps) {
  // Local state for immediate input responsiveness
  const [localValue, setLocalValue] = useState(value);

  // Sync local state when parent value changes (e.g., external clear)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounced update to parent
  useEffect(() => {
    // Skip if local value matches parent value (prevents initial trigger)
    if (localValue === value) return;

    const timer = setTimeout(() => {
      onValueChange(localValue);
    }, delay);

    return () => clearTimeout(timer);
  }, [localValue, delay, onValueChange, value]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  const handleClear = useCallback(() => {
    setLocalValue('');
    onValueChange('');
  }, [onValueChange]);

  return (
    <div className={cn('relative', className)}>
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        type="text"
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
        aria-label={ariaLabel}
        className={cn('pl-8', inputClassName)}
      />
      {localValue && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          // Icon-only control ⇒ needs an accessible name (UI Consistency gate §5). It had none,
          // so every one of this primitive's consumers shipped an unlabelled "X" to screen
          // readers. No tooltip: a clear affordance inside a field it is adjacent to reads
          // unambiguously, and a tooltip over a text input fights the caret.
          aria-label="Clear search"
          title="Clear search"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
          onClick={handleClear}
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
});
